import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Sparkles, Check, Loader2, Hammer, Home, Wind, Shield } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import type { QuoteWizardState } from "../DoorKnockerQuickQuote";

interface Props {
  state: QuoteWizardState;
  setState: React.Dispatch<React.SetStateAction<QuoteWizardState>>;
  onNext: () => void;
}

const TRADES = [
  { id: "Roofing", icon: Home, label: "Roofing" },
  { id: "Gutters", icon: Wind, label: "Gutters" },
  { id: "Siding", icon: Shield, label: "Siding" },
  { id: "Windows", icon: Hammer, label: "Windows" },
];

const TIERS: Array<{ id: "Good" | "Better" | "Best"; label: string; desc: string }> = [
  { id: "Good", label: "Good", desc: "Value tier" },
  { id: "Better", label: "Better", desc: "Most popular" },
  { id: "Best", label: "Best", desc: "Premium build" },
];

export function StepTrades({ state, setState, onNext }: Props) {
  const [loadingItems, setLoadingItems] = useState(false);
  const [items, setItems] = useState<any[] | null>(null);
  const [subtotal, setSubtotal] = useState<number | null>(null);

  const toggleTrade = (id: string) => {
    setState((s) => ({
      ...s,
      trades: s.trades.includes(id) ? s.trades.filter((t) => t !== id) : [...s.trades, id],
    }));
    setItems(null);
  };

  const suggestItems = async () => {
    if (state.trades.length === 0) {
      toast.error("Pick at least one trade");
      return;
    }
    setLoadingItems(true);
    try {
      const allItems: any[] = [];
      let total = 0;
      for (const trade of state.trades) {
        const { data, error } = await supabase.functions.invoke(
          "instant-quote-suggest-items",
          {
            body: {
              trade,
              totalSquares: 25,
              address: state.address,
              options: { tier: state.tier },
            },
          }
        );
        if (error) throw error;
        if (data?.line_items) {
          allItems.push(...data.line_items.map((it: any) => ({ ...it, trade })));
          total += data.subtotal || 0;
        }
      }
      setItems(allItems);
      setSubtotal(total);
      toast.success(`AI suggested ${allItems.length} line items`);
    } catch (e: any) {
      console.error(e);
      toast.error(e.message || "Failed to suggest line items");
    } finally {
      setLoadingItems(false);
    }
  };

  return (
    <div className="px-5 pt-6 pb-8 space-y-6">
      <div>
        <div className="text-xs font-semibold text-primary uppercase tracking-wider mb-1.5">Step 3 / 4</div>
        <h2 className="text-2xl font-bold tracking-tight">What's the scope?</h2>
        <p className="text-sm text-muted-foreground mt-1">Pick trades and let AI build the line items.</p>
      </div>

      {/* Trades grid */}
      <div className="grid grid-cols-2 gap-2.5">
        {TRADES.map((t) => {
          const active = state.trades.includes(t.id);
          return (
            <button
              key={t.id}
              onClick={() => toggleTrade(t.id)}
              className={cn(
                "relative flex items-center gap-3 rounded-2xl border-2 p-3.5 transition-all active:scale-[0.97]",
                active
                  ? "border-primary bg-primary/10 shadow-[0_0_0_4px_hsl(var(--primary)/0.1)]"
                  : "border-border bg-card hover:border-border/80"
              )}
            >
              <span
                className={cn(
                  "flex h-9 w-9 items-center justify-center rounded-xl",
                  active ? "bg-primary text-primary-foreground" : "bg-muted text-foreground"
                )}
              >
                <t.icon className="h-4 w-4" />
              </span>
              <span className="text-sm font-semibold">{t.label}</span>
              {active && <Check className="absolute top-2 right-2 h-3.5 w-3.5 text-primary" />}
            </button>
          );
        })}
      </div>

      {/* Tier selector */}
      <div>
        <div className="text-xs font-semibold text-muted-foreground mb-2">Pricing tier</div>
        <div className="grid grid-cols-3 gap-2">
          {TIERS.map((tier) => {
            const active = state.tier === tier.id;
            return (
              <button
                key={tier.id}
                onClick={() => {
                  setState((s) => ({ ...s, tier: tier.id }));
                  setItems(null);
                }}
                className={cn(
                  "rounded-xl border-2 p-3 text-center transition-all active:scale-[0.97]",
                  active ? "border-primary bg-primary/10" : "border-border bg-card"
                )}
              >
                <div className="text-sm font-bold">{tier.label}</div>
                <div className="text-[10px] text-muted-foreground mt-0.5">{tier.desc}</div>
              </button>
            );
          })}
        </div>
      </div>

      {/* AI Suggest button */}
      <Button
        onClick={suggestItems}
        disabled={loadingItems || state.trades.length === 0}
        variant={items ? "outline" : "default"}
        className="w-full h-12 text-base font-semibold gap-2"
      >
        {loadingItems ? (
          <>
            <Loader2 className="h-4 w-4 animate-spin" />
            AI is building your estimate…
          </>
        ) : items ? (
          <>
            <Sparkles className="h-4 w-4" />
            Re-suggest with AI
          </>
        ) : (
          <>
            <Sparkles className="h-4 w-4" />
            AI Suggest Line Items
          </>
        )}
      </Button>

      {/* Items preview */}
      {items && items.length > 0 && (
        <div className="rounded-2xl border border-border/60 bg-card overflow-hidden">
          <div className="px-4 py-3 border-b border-border/40 flex items-center justify-between">
            <div className="text-sm font-bold">{items.length} items</div>
            <div className="text-sm font-bold tabular-nums">
              ${subtotal?.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </div>
          </div>
          <div className="max-h-56 overflow-y-auto divide-y divide-border/40">
            {items.slice(0, 8).map((it, i) => (
              <div key={i} className="px-4 py-2.5 flex items-center justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium truncate">{it.description}</div>
                  <div className="text-[10px] text-muted-foreground">
                    {it.quantity} {it.unit} · {it.category}
                  </div>
                </div>
                <div className="text-xs font-semibold tabular-nums">
                  ${Number(it.total).toLocaleString(undefined, { maximumFractionDigits: 0 })}
                </div>
              </div>
            ))}
            {items.length > 8 && (
              <div className="px-4 py-2 text-[10px] text-muted-foreground text-center">
                + {items.length - 8} more items
              </div>
            )}
          </div>
        </div>
      )}

      <Button onClick={onNext} className="w-full h-12 text-base font-semibold" size="lg">
        Continue to Template
      </Button>
    </div>
  );
}
